import { formatMoney } from "@commerce/shopper/types";
import { z } from "zod";
import type { OrderDetail, OrderQueue, OrderRow, OrderStatus } from "./types";

/** The Orders tabs, in the order an order moves through them. */
export const ORDER_QUEUES = [
	"new",
	"preparing",
	"on_the_way",
	"done",
	"cancelled",
] as const satisfies readonly OrderQueue[];

export const QUEUE_LABEL: Record<OrderQueue, string> = {
	new: "New",
	preparing: "Preparing",
	on_the_way: "On the way",
	done: "Done",
	cancelled: "Cancelled",
};

/** Queues whose count is work waiting: the tab shows it. Done and Cancelled only grow. */
export const COUNTED_QUEUES: ReadonlySet<OrderQueue> = new Set([
	"new",
	"preparing",
	"on_the_way",
]);

export const ordersSearchSchema = z.object({
	queue: z.enum(ORDER_QUEUES).optional(),
	/** One branch's orders. */
	store: z.string().optional(),
	q: z.string().optional(),
	/** The order open in the detail pane. */
	id: z.string().optional(),
});
export type OrdersSearch = z.infer<typeof ordersSearchSchema>;

export function listInputFrom(organizationId: string, search: OrdersSearch) {
	return {
		organizationId,
		queue: search.queue ?? "new",
		...(search.store ? { storeId: search.store } : {}),
		...(search.q ? { q: search.q } : {}),
	};
}

type BadgeVariant =
	| "default"
	| "secondary"
	| "outline"
	| "success"
	| "warning"
	| "destructive"
	| "info";

export const STATUS_META: Record<
	OrderStatus,
	{ label: string; variant: BadgeVariant }
> = {
	PLACED: { label: "New", variant: "warning" },
	CONFIRMED: { label: "Accepted", variant: "info" },
	IN_PROGRESS: { label: "Picking", variant: "info" },
	READY_FOR_PICKUP: { label: "Ready for pickup", variant: "info" },
	OUT_FOR_DELIVERY: { label: "On the way", variant: "info" },
	DELIVERED: { label: "Delivered", variant: "success" },
	CANCELLED: { label: "Cancelled", variant: "destructive" },
	RETURNED: { label: "Returned", variant: "secondary" },
};

/** Statuses an order no longer moves out of. */
const FINAL: ReadonlySet<OrderStatus> = new Set([
	"DELIVERED",
	"CANCELLED",
	"RETURNED",
]);

export const isFinal = (status: OrderStatus) => FINAL.has(status);

/**
 * The one main action of an order, by its state (spec 5.3): New → Accept (or confirm the
 * address first when the shopper only typed an area), Accepted → Start picking, Picking →
 * Out for delivery or Ready for pickup, then Delivered. Null when nothing moves it on here:
 * a final status, or an order the retailer's own system reported (it updates it there).
 */
export type MainAction =
	| { kind: "confirm_address"; label: string }
	| { kind: "accept"; label: string }
	| { kind: "status"; status: OrderStatus; label: string; done: string };

export function mainAction(
	order: Pick<
		OrderRow,
		"status" | "origin" | "fulfilment" | "addressNeedsConfirmation"
	>,
): MainAction | null {
	if (order.origin !== "PLACED") {
		return null;
	}
	switch (order.status) {
		case "PLACED":
			return order.addressNeedsConfirmation &&
				order.fulfilment !== "PICKUP"
				? { kind: "confirm_address", label: "Confirm the address" }
				: { kind: "accept", label: "Accept" };
		case "CONFIRMED":
			return {
				kind: "status",
				status: "IN_PROGRESS",
				label: "Start picking",
				done: "Picking started",
			};
		case "IN_PROGRESS":
			return order.fulfilment === "PICKUP"
				? {
						kind: "status",
						status: "READY_FOR_PICKUP",
						label: "Ready for pickup",
						done: "Marked ready for pickup",
					}
				: {
						kind: "status",
						status: "OUT_FOR_DELIVERY",
						label: "Out for delivery",
						done: "Marked out for delivery",
					};
		case "READY_FOR_PICKUP":
			return {
				kind: "status",
				status: "DELIVERED",
				label: "Picked up",
				done: "Marked picked up",
			};
		case "OUT_FOR_DELIVERY":
			return {
				kind: "status",
				status: "DELIVERED",
				label: "Delivered",
				done: "Marked delivered",
			};
		default:
			return null;
	}
}

/** Why Accept is not offered yet, for the disabled state and its tooltip. */
export function acceptBlockedReason(
	order: Pick<OrderRow, "status" | "fulfilment" | "addressNeedsConfirmation">,
): string | null {
	if (order.status !== "PLACED") {
		return null;
	}
	return order.addressNeedsConfirmation && order.fulfilment !== "PICKUP"
		? "The shopper only typed an area. Set the delivery point first."
		: null;
}

/** What the menu offers besides the main action. */
export function secondaryActions(
	order: Pick<
		OrderDetail,
		"status" | "origin" | "paymentMethod" | "paymentStatus" | "events"
	>,
) {
	const placed = order.origin === "PLACED";
	const open = !isFinal(order.status);
	return {
		reject: placed && order.status === "PLACED",
		cancel: placed && open && order.status !== "PLACED",
		editLines:
			placed &&
			(order.status === "PLACED" ||
				order.status === "CONFIRMED" ||
				order.status === "IN_PROGRESS"),
		paymentLink:
			placed &&
			open &&
			order.paymentMethod === "link" &&
			order.paymentStatus !== "PAID",
		resend: placed && order.events.some((e) => e.shopperMessage),
	};
}

/** "1042", or the retailer's id for a reported order. */
export const orderName = (
	order: Pick<OrderRow, "number" | "externalOrderId">,
) => (order.number !== null ? String(order.number) : order.externalOrderId);

export const money = (amount: string | null, currency: string) =>
	amount === null ? null : formatMoney(amount, currency);

export const PAYMENT_LABEL: Record<string, string> = {
	cash: "Cash",
	card_on_delivery: "Card on delivery",
	link: "Payment link",
};

export const PAYMENT_STATUS_LABEL: Record<OrderRow["paymentStatus"], string> = {
	NOT_REQUIRED: "",
	PENDING: "Not paid yet",
	PAID: "Paid",
	FAILED: "Payment failed",
	REFUNDED: "Refunded",
};

export const SURFACE_LABEL: Record<string, string> = {
	flow: "checkout form",
	buttons: "buttons",
	web: "web checkout",
	call: "a call",
};

export const EVENT_LABEL: Record<string, string> = {
	placed: "Placed",
	accepted: "Accepted",
	rejected: "Rejected",
	status: "Status changed",
	lines_edited: "Items changed",
	note: "Note",
	message_sent: "Shopper told",
	message_failed: "Couldn't tell the shopper",
	payment: "Payment",
	issue_reported: "Shopper reported a problem",
	adapter_error: "Order system error",
};

export const ACTOR_LABEL: Record<string, string> = {
	staff: "Team",
	shopper: "Shopper",
	retailer: "Your system",
	system: "Hephon",
	agent: "Agent",
	adapter: "Order system",
};

/** "Al Reem · Today 14:00-15:00" pieces: the slot as one label in the panel's clock. */
export function slotLabel(
	start: Date | null,
	end: Date | null,
	clockOf: (d: Date) => string,
): string | null {
	if (!start) {
		return null;
	}
	return end ? `${clockOf(start)}-${clockOf(end)}` : clockOf(start);
}

/** Lat/lng from a Google Maps link or typed coordinates ("33.89, 35.50"). */
export function parsePoint(
	text: string,
): { latitude: number; longitude: number } | null {
	const patterns = [
		/@(-?\d{1,2}\.\d+),\s*(-?\d{1,3}\.\d+)/,
		/[?&](?:q|query|ll|destination)=(-?\d{1,2}\.\d+)(?:,|%2C)\s*(-?\d{1,3}\.\d+)/i,
		/!3d(-?\d{1,2}\.\d+)!4d(-?\d{1,3}\.\d+)/,
		/^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/,
	];
	for (const pattern of patterns) {
		const match = pattern.exec(text);
		if (match) {
			const latitude = Number(match[1]);
			const longitude = Number(match[2]);
			if (Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180) {
				return { latitude, longitude };
			}
		}
	}
	return null;
}

export const mapsLink = (latitude: number, longitude: number) =>
	`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

export const mapsSearchLink = (text: string) =>
	`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;

/**
 * Order lines with a React key each: the catalog id, else the item id, else the title. Lines
 * have no id of their own; a repeat of the same key gets "#2", "#3" in the order given.
 */
export function keyedLines<
	Line extends {
		catalogId: string | null;
		itemId: string | null;
		title: string;
	},
>(lines: readonly Line[]): { key: string; line: Line }[] {
	const seen = new Map<string, number>();
	return lines.map((line) => {
		const base = line.catalogId ?? line.itemId ?? line.title;
		const count = (seen.get(base) ?? 0) + 1;
		seen.set(base, count);
		return { key: count === 1 ? base : `${base}#${count}`, line };
	});
}
